/**
 * Hook for coordinating date range changes with data loading
 * 
 * This hook ensures that when the date range changes, all relevant data
 * (events, trend charts) is reloaded with the new date range.
 */

import { useEffect, useCallback } from 'react';
import { useDateRange } from './use-date-range';
import { useEquipmentData } from './use-equipment-state';

/**
 * Hook that coordinates date range changes with data reloading
 */
export const useDateRangeCoordination = () => {
  const { dateRange } = useDateRange();
  const { loadEvents, loadTrendChartData, equipmentHierarchy, isLoading, loadKPIData } = useEquipmentData();
  
  // Reload data when date range changes - but only if hierarchy is loaded
  useEffect(() => {
    // Don't load data if hierarchy is still loading or not available
    if (!equipmentHierarchy || isLoading.hierarchy) {
      console.log('useDateRangeCoordination: Skipping data load - hierarchy not ready');
      return;
    }
    
    // Debounce the data loading to avoid excessive API calls
    const timeoutId = setTimeout(() => {
      console.log('useDateRangeCoordination: Loading data for date range change');
      loadEvents();
      loadTrendChartData();
      loadKPIData();
    }, 300); // 300ms debounce
    
    return () => clearTimeout(timeoutId);
  }, [dateRange, loadEvents, loadTrendChartData, equipmentHierarchy, isLoading.hierarchy]);
  
  // Manual refresh function
  const refreshDataForDateRange = useCallback(() => {
    loadEvents();
    loadTrendChartData();
    loadKPIData()
  }, [loadEvents, loadTrendChartData, loadKPIData]);
  
  return {
    dateRange,
    refreshDataForDateRange
  };
};

/**
 * Hook for date range change notifications
 * Useful for components that need to react to date range changes
 */
export const useDateRangeChangeNotification = (
  onDateRangeChange?: (dateRange: { startDate: string; endDate: string }) => void
) => {
  const { dateRange } = useDateRange();
  
  useEffect(() => {
    if (onDateRangeChange) {
      onDateRangeChange(dateRange);
    }
  }, [dateRange, onDateRangeChange]);
  
  return { dateRange };
};